import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { blogPosts as mockBlogPosts } from '../data/mock';
import { getBlogPosts } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const BlogSection = () => {
  const [blogPosts, setBlogPosts] = useState(mockBlogPosts);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();

  const getTranslated = (obj, field) => {
    if (!obj) return '';
    if (language === 'en' && obj[`${field}_en`]) {
      return obj[`${field}_en`];
    }
    return obj[field] || '';
  };

  useEffect(() => {
    const fetchBlogPosts = async () => {
      const posts = await getBlogPosts();
      if (posts && posts.length > 0) {
        setBlogPosts(posts);
      }
      setLoading(false);
    };
    
    fetchBlogPosts();
  }, []);

  // Show only first 3 posts as preview
  const previewPosts = blogPosts.slice(0, 3);

  return (
    <section id="blog" className="py-24 bg-brand-soft">
      <div className="container mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-display font-light text-brand-text mb-6">{t.blogTitle}</h2>
          <p className="text-lg font-body text-brand-text-secondary max-w-3xl mx-auto">
            {t.blogSubtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {previewPosts.map((post) => (
            <Card 
              key={post.id} 
              className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border border-brand-accent/30 cursor-pointer bg-white"
              onClick={() => navigate(`/blog/${post.slug || post.id}`)}
            >
              <div className="aspect-video overflow-hidden">
                <img 
                  src={post.image} 
                  alt={getTranslated(post, 'title')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <CardContent className="p-8 bg-white">
                <div className="text-xs font-body tracking-widest text-brand-primary mb-3 uppercase">{getTranslated(post, 'category')}</div>
                <h3 className="text-xl font-display font-medium text-brand-text mb-4 line-clamp-2">
                  {getTranslated(post, 'title')}
                </h3>
                <p className="text-brand-text-secondary font-body text-sm mb-6 line-clamp-3 leading-relaxed">{getTranslated(post, 'excerpt')}</p>
                <Button variant="ghost" className="group/btn p-0 h-auto text-brand-primary hover:text-brand-primary-hover">
                  <span className="text-sm font-body">{t.readMore}</span>
                  <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Botão Ver Todos */}
        <div className="text-center mt-12">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-brand-primary hover:text-brand-primary-hover font-body font-medium text-lg transition-colors group"
          >
            {language === 'en' ? 'See All Articles' : 'Ver Todos os Artigos'}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;